import { SnackbarKey, useSnackbar as useNotistackSnackbar } from 'notistack'
import { useCallback, useMemo } from 'react'

type UseSnackbarReturnType = {
  enqueueErrorStack: (message: string, errorCode?: number | string, errorMessage?: string) => void
  enqueueSuccessStack: (message: string) => void
  enqueueWarningStack: (message: string) => void
  enqueueInfoStack: (message: string) => void
  enqueueLoadingStack: (message: string) => SnackbarKey

  /**
   * Ensures an Error will never been overseen. Enqueue the message of an Error to the snackbar.
   * Should only be used for situations where no specific error-handling is defined yet
   *
   * @param error
   */
  snackError: (error: Error) => void
}

/**
 * custom hook to wrap the snackbar
 */
export const useSnackbar = (): UseSnackbarReturnType => {
  const { enqueueSnackbar } = useNotistackSnackbar()

  const snackError = useMemo(
    () =>
      (error: Error): void => {
        console.error(error)
        enqueueSnackbar(error.message, {
          variant: 'error',
        })
      },
    [enqueueSnackbar]
  )

  const enqueueErrorStack = useCallback(
    (message: string, errorCode?: number | string, errorMessage?: string): void => {
      let snackbarMessage = message

      if (errorCode) {
        snackbarMessage += `. Error: ${errorCode}`
        if (errorMessage) {
          snackbarMessage += ` - ${errorMessage}`
        }
      }
      if (!errorCode && errorMessage) {
        snackbarMessage += `. Error: ${errorMessage}`
      }

      enqueueSnackbar(snackbarMessage, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'center',
        },
      })
    },
    [enqueueSnackbar]
  )

  const enqueueSuccessStack = useMemo(
    () =>
      (message: string): void => {
        enqueueSnackbar(message, {
          variant: 'success',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        })
      },
    [enqueueSnackbar]
  )

  const enqueueWarningStack = useMemo(
    () =>
      (message: string): void => {
        enqueueSnackbar(message, {
          variant: 'warning',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        })
      },
    [enqueueSnackbar]
  )

  const enqueueInfoStack = useMemo(
    () =>
      (message: string): void => {
        enqueueSnackbar(message, {
          variant: 'info',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
        })
      },
    [enqueueSnackbar]
  )

  const enqueueLoadingStack = useMemo(
    () =>
      (message: string): SnackbarKey => {
        const snackKey = enqueueSnackbar(message, {
          variant: 'info',
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          },
          persist: true,
        })
        return snackKey
      },
    [enqueueSnackbar]
  )

  return { enqueueErrorStack, enqueueSuccessStack, enqueueWarningStack, enqueueInfoStack, snackError, enqueueLoadingStack }
}
